import { useTeamRecentGames, calculateRecentStats } from "@/hooks/useTeamRecentGames";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";

interface TeamRecentGamesDisplayProps {
  teamId: string;
  teamName: string;
  teamLogo?: string;
  location?: 'home' | 'away' | 'all';
  title?: string;
}

export const TeamRecentGamesDisplay = ({
  teamId,
  teamName,
  teamLogo,
  location = 'all',
  title
}: TeamRecentGamesDisplayProps) => {
  const { data: games, isLoading } = useTeamRecentGames({
    teamId,
    location,
    limit: 5
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        <h3 className="font-semibold text-sm">{title || teamName}</h3>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-12 bg-muted/50 animate-pulse rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!games || games.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="font-semibold text-sm">{title || teamName}</h3>
        <p className="text-sm text-muted-foreground">No recent games</p>
      </div>
    );
  }

  const stats = calculateRecentStats(games);

  const getResultBadgeClass = (result: 'W' | 'D' | 'L') => {
    if (result === 'W') return 'bg-green-500 hover:bg-green-600 text-white border-green-600';
    if (result === 'D') return 'bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-600';
    return 'bg-red-500 hover:bg-red-600 text-white border-red-600';
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {teamLogo && (
            <img 
              src={teamLogo} 
              alt={teamName}
              className="w-5 h-5 object-contain"
            />
          )}
          <h3 className="font-semibold text-sm">{title || teamName}</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{stats.wins}W</span>
          <span className="font-semibold text-foreground">{stats.draws}D</span>
          <span className="font-semibold text-foreground">{stats.losses}L</span>
        </div>
      </div>

      <div className="space-y-2">
        {games.map((game) => (
          <div
            key={game.fixture_id}
            className="flex items-center justify-between p-2 rounded bg-card border text-xs"
          >
            <span className="text-muted-foreground shrink-0 w-12">
              {format(new Date(game.match_date), 'dd.MM', { locale: enUS })}
            </span>
            
            <div className="flex items-center gap-1 shrink-0">
              {game.home_team_logo && (
                <img 
                  src={game.home_team_logo} 
                  alt={game.home_team_name}
                  className="w-4 h-4 object-contain"
                />
              )}
              <span className="font-medium text-[10px]">
                {game.home_team_tla}
              </span>
            </div>
            
            <span className="font-bold mx-2">
              {game.home_score} - {game.away_score}
            </span>
            
            <div className="flex items-center gap-1 shrink-0">
              <span className="font-medium text-[10px]">
                {game.away_team_tla}
              </span>
              {game.away_team_logo && (
                <img 
                  src={game.away_team_logo} 
                  alt={game.away_team_name}
                  className="w-4 h-4 object-contain"
                />
              )}
            </div>
            
            <Badge 
              className={`w-6 h-6 flex items-center justify-center p-0 text-xs font-bold ml-2 ${getResultBadgeClass(game.result)}`}
            >
              {game.result}
            </Badge>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs pt-2 border-t">
        <span className="text-muted-foreground">Form:</span>
        <div className="flex gap-1">
          {stats.form.split('-').map((result, idx) => (
            <Badge 
              key={idx}
              className={`w-5 h-5 flex items-center justify-center p-0 text-[10px] font-bold ${getResultBadgeClass(result as 'W' | 'D' | 'L')}`}
            >
              {result}
            </Badge>
          ))}
        </div>
      </div>

      <div className="text-xs text-muted-foreground pt-1">
        Goals: {stats.goalsFor} scored, {stats.goalsAgainst} conceded
      </div>
    </div>
  );
};
